import * as React from "react";
import {Grid2 as Grid} from "@mui/material";
import {ExtendRequestDatesDTO, RequestDTO} from "@/api/data-contracts";
import {requestApi} from "@/boxhub-api";
import {ObjectSchema} from "yup";
import {ModelAction, useModelState} from "@/lib/modelstate";
import {setField} from "@/lib/accessor";
import {isDate} from "@/lib/utils";
import {calcDeliveryDateTo} from "@/model/request";
import dayjs from "dayjs";
import {ExtendDatesMinDates, useExtendDatesSchema} from "@/pages/internal-request-page/schema";
import {useRequestDatesValidator} from "@/pages/internal-request-page/useRequestDatesValidator";
import {useSnackbarEx} from "@/lib/snackbarex";
import {useTranslation} from "react-i18next";
import {CommonI18N, RequestI18N} from "@/translations";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import RequestDateFields from "@/common/request-cards/RequestDateFields";

interface ExtendDatesDialogProps {
    open: boolean;
    onClose: () => void;
    model: RequestDTO;
    onSuccess: (updatedRequest: RequestDTO) => void;
}

/**
 * Reducer for the extend-dates dialog model state.
 * Mirrors the deliveryDateFrom auto-copy logic from requestReducer,
 * adapted for the flat ExtendRequestDatesDTO field structure.
 *
 * @param model - The current dialog model state.
 * @param action - The model action dispatched.
 * @returns The updated model state.
 */
const extendDatesReducer = (model: ExtendRequestDatesDTO, action: ModelAction): ExtendRequestDatesDTO => {
    if (action.type !== "--onchange") return model;
    const field = action.data[0];
    if (field === "deliveryDateFrom" && isDate(model.deliveryDateFrom)) {
        return setField(model, "deliveryDateTo", calcDeliveryDateTo(model.deliveryDateFrom, model.deliveryDateTo));
    }
    if (field === "deliveryDateTo" && isDate(model.deliveryDateFrom) && isDate(model.deliveryDateTo)) {
        const differenceInDays = dayjs(model.deliveryDateTo).diff(dayjs(model.deliveryDateFrom), "day");
        if (differenceInDays < 0) {
            return setField(model, "deliveryDateFrom", model.deliveryDateTo);
        }
    }
    return model;
};

/**
 * Dialog for extending the validity and delivery dates of a request.
 * Contains its own model state and Yup schema for the three date fields.
 * On save, calls the extendRequestDates API and notifies the parent via onSuccess.
 *
 * @param open - Whether the dialog is visible.
 * @param onClose - Callback to close the dialog without saving.
 * @param model - The current request, used to initialize and constrain the date fields.
 * @param onSuccess - Callback invoked with the updated request after a successful save.
 */
const ExtendDatesDialog: React.FC<ExtendDatesDialogProps> = ({ open, onClose, model, onSuccess }) => {
    const { t } = useTranslation();
    const { showMessage } = useSnackbarEx();
    const [processing, setProcessing] = React.useState(false);

    const minDates: ExtendDatesMinDates = React.useMemo(
        () => ({
            validUntil: (model?.validUntil as string) ?? null,
            deliveryDateFrom: (model?.orderData?.deliveryDateFrom as string) ?? null,
            deliveryDateTo: (model?.orderData?.deliveryDateTo as string) ?? null
        }),
        [model?.validUntil, model?.orderData?.deliveryDateFrom, model?.orderData?.deliveryDateTo]
    );

    const schema = useExtendDatesSchema(minDates) as unknown as ObjectSchema<ExtendRequestDatesDTO>;

    const validateDates = useRequestDatesValidator(false);

    const {
        model: dto,
        errors,
        onChange,
        validate
    } = useModelState({
        initialState: {
            validUntil: (model?.validUntil as string) ?? "",
            deliveryDateFrom: (model?.orderData?.deliveryDateFrom as string) ?? "",
            deliveryDateTo: (model?.orderData?.deliveryDateTo as string) ?? ""
        } as ExtendRequestDatesDTO,
        schema,
        reducer: extendDatesReducer,
        validator: async (dto) =>
            validateDates({
                deliveryDateFrom: dto.deliveryDateFrom,
                deliveryDateTo: dto.deliveryDateTo,
                validUntil: dto.validUntil
            })
    });

    const handleSave = async () => {
        if (!model.id) return;
        if (!validate()) return;
        setProcessing(true);
        try {
            const response = await requestApi.extendRequestDates(model.id, dto);
            showMessage({summary: t(RequestI18N.details.message.extendDatesSuccess), severity: "success"});
            onSuccess(response.data);
            onClose();
        } catch (error: unknown) {
            showMessage({summary: t(CommonI18N.message.unknownError), severity: "error", error});
        } finally {
            setProcessing(false);
        }
    };

    return (
        <ConfirmationDialog
            open={open}
            onClose={onClose}
            onConfirm={handleSave}
            title={t(RequestI18N.details.action.extendDates)}
            message=""
            confirmButtonText={t(RequestI18N.details.action.extendDatesSave)}
            cancelButtonText={t(CommonI18N.action.cancel)}
            confirmDisabled={processing}
            autoFocusConfirm={false}
            maxWidth="sm"
            fullWidth
        >
            <Grid container spacing={2} direction="row" paddingTop={1}>
                <RequestDateFields
                    nameValidUntil="validUntil"
                    nameDeliveryDateFrom="deliveryDateFrom"
                    nameDeliveryDateTo="deliveryDateTo"
                    validUntil={dto.validUntil}
                    deliveryDateFrom={dto.deliveryDateFrom}
                    deliveryDateTo={dto.deliveryDateTo}
                    errors={{
                        validUntil: errors?.validUntil,
                        deliveryDateFrom: errors?.deliveryDateFrom,
                        deliveryDateTo: errors?.deliveryDateTo
                    }}
                    onChange={onChange}
                    minDates={minDates}
                />
            </Grid>
        </ConfirmationDialog>
    );
};

export default ExtendDatesDialog;
